import { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import styled from 'styled-components'
import HorizontalScrollTabs, { TabType } from '@/components/common/tabs/scroll-tab'
import Text from '@/components/common/typeface/Text'
import EventCouponDownloadModal from '@/components/domains/events/EventCouponDownloadModal'
import EventDetailSection from '@/components/domains/events/EventDetailSection'
import { Anchor } from '@/components/domains/home/Anchor'
import { ModalSharedPageAppLanding } from '@/components/domains/modals'
import { Z_INDEXES } from '@/constants/legacy/constLayout'
import { EventContainer } from '@/containers/contexts/regional/event.container'
import useEventDetailEventTracker from '@/containers/event-tracker/event-detail.event-tracker'
import { sectionTopPosition } from '@/utils/event-detail.util'
import { getImageUrl } from '@/utils/utilURL'
import EventDetailCouponDownloadButton from './EventDetailCouponDownloadButton'
import EventDetailDescription from './EventDetailDescription'

const EventDetailContentV4 = () => {
  const { ad } = EventContainer.useContainer()
  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const sectionContainerRef = useRef<HTMLDivElement>(null) // 컨테이너내에 있는 모든 섹션 Element를 가져오는 용도
  const sectionsRef = useRef([] as Array<HTMLElement>) // 섹션 위치로 스크롤하기 위해서 위치 저장용도
  const { query } = useRouter()

  const [topNavigationHeight, setTopNavigationHeight] = useState(0)

  const { trackClickEventsaleDetailSection } = useEventDetailEventTracker()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const handleOpenModal = () => {
    setIsModalOpen(true)
    document.body.style.overflow = 'hidden'
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    document.body.style.overflow = 'auto'
  }

  // sticky의 top위치를 정하기 위해서 header의 높이가 필요함
  useEffect(() => {
    const handleResize = () => {
      const topNavigationHeight = document.querySelector('header')?.clientHeight
      setTopNavigationHeight(topNavigationHeight ?? 0)
    }

    handleResize()
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  useEffect(() => {
    // 스크롤 이동을 위해서 각 섹션의 ref를 저장
    sectionContainerRef.current?.querySelectorAll('.section-container').forEach((el, i) => {
      sectionsRef.current[i] = el as HTMLElement
    })

    // 스크롤시 Active Tab Index 변경
    const updateActiveTab = () => {
      const invalidIndex = -1
      const currentScrollPosition = window.scrollY
      const foundIndex = sectionsRef.current.reduce((acc, cur, i) => {
        return currentScrollPosition >= sectionTopPosition(cur.offsetTop, 80) ? i : acc
      }, invalidIndex)

      if (foundIndex !== invalidIndex) {
        setActiveTabIndex(foundIndex)
      }
    }

    const onScroll = () => {
      updateActiveTab()
    }

    window.addEventListener('scroll', onScroll)

    return () => {
      window.removeEventListener('scroll', onScroll)
    }
  }, [])

  const onClickTab = ({ tabId, tabName, index }: TabType) => {
    location.replace(`#${tabId}`)
    trackClickEventsaleDetailSection({
      adId: ad.id,
      adName: ad.name,
      adSlug: ad.slug ?? '',
      tabName: tabName,
      sectionIndex: index,
    })
  }

  const filterEmptyDisplayCollections =
    ad?.adDisplayCollections?.edges.filter((section) => section?.node?.displayProductInfos?.totalCount) || []

  const isMultipleTab = filterEmptyDisplayCollections && filterEmptyDisplayCollections.length > 1

  // 마지막 섹션 상품 갯수
  const lastSectionProductCount =
    ad?.adDisplayCollections?.edges[ad?.adDisplayCollections?.edges.length - 1]?.node?.displayProductInfos
      ?.totalCount || 0

  return (
    <StyledContainer>
      {/* 쿠폰 다운로드 */}
      <EventDetailCouponDownloadButton openModal={handleOpenModal} />

      {/* 이벤트 상세 */}
      <EventDetailDescription />
      <div
        style={{
          /* TopNavigation 아래에 sticky 시키기 위해서 사용 */
          zIndex: Z_INDEXES.HEADER,
          position: 'sticky',
          top: topNavigationHeight + 'px',
        }}
      >
        {isMultipleTab && (
          <HorizontalScrollTabs
            tabs={filterEmptyDisplayCollections.map((item) => ({
              id: item.node?.id ?? '',
              tabName: item.node?.tabName ?? '',
            }))}
            activeTabIndex={activeTabIndex}
            onClickTab={onClickTab}
          />
        )}
      </div>
      <div ref={sectionContainerRef}>
        {ad?.adDisplayCollections?.edges.map((item, index) => {
          if (!item?.node?.id) {
            return null
          }
          const hasProduct = query.isSecret === 'true' ? true : !!item?.node?.displayProductInfos?.totalCount

          return (
            <StyledSection
              key={item.node.id}
              className={hasProduct ? 'section-container' : ''}
              isLastSectionHasProduct={!!lastSectionProductCount}
              hasProduct={hasProduct}
            >
              <Anchor id={item.node.id} top={0} />
              {/* 이미지 영역 */}
              {item.node.backgroundImage && (
                <img src={getImageUrl(item.node.backgroundImage)} alt={item.node.name} className="section-background" />
              )}

              {hasProduct && (
                <div className="section-detail">
                  {/* 타이틀 */}
                  <StyledSectionTitle>{item.node.name}</StyledSectionTitle>
                  {/* 상품리스트, 상품갯수, 정렬 */}
                  <EventDetailSection
                    sectionIndex={index}
                    displayCollectionId={item.node.id}
                    displayCollectionTabName={item.node.tabName ?? ''}
                  />
                </div>
              )}
            </StyledSection>
          )
        })}
      </div>
      <ModalSharedPageAppLanding />
      <EventCouponDownloadModal isModalOpen={isModalOpen} closeModal={handleCloseModal} />
    </StyledContainer>
  )
}

const StyledContainer = styled.div`
  position: relative;

  > p {
    margin: 0;
  }
`
const StyledSection = styled.section<{ isLastSectionHasProduct: boolean; hasProduct: boolean }>`
  position: relative;

  /* margin 말고 padding으로 잡아야 전체 사이즈 가져갈수 있음 */
  padding-bottom: ${({ hasProduct }) => (hasProduct ? '0.8rem' : '')};

  .section-detail {
    padding: 0 1.6rem;
  }

  img.section-background {
    width: 100%;
  }
  &:last-child {
    margin-bottom: ${({ isLastSectionHasProduct }) =>
      isLastSectionHasProduct ? 'calc(20.9rem + env(safe-area-inset-bottom))' : 'env(safe-area-inset-bottom)'};
  }
`

const StyledSectionTitle = styled(Text.H5)`
  margin-top: 3.2rem;
  margin-bottom: 1.6rem;
  white-space: pre;
`

export default EventDetailContentV4
